import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'

import { usePreferencesStore, PreferenceKey } from '@/entities/preference'

import { getAppURL } from '@/shared/utils/electron'
import { useFetchApi } from '@/shared/utils/fetch'

import {
  Terminal,
  TerminalFields,
  TerminalFieldPayload,
  TerminalFieldsKeys,
  OutgoingCallsHandlingConditions,
  WEB_CLIENT_TERMINAL_ID,
} from '..'

export const useStoreSettingsTerminals = defineStore('terminals', () => {
  const terminals = ref<Array<Terminal>>([])

  const preferencesStore = usePreferencesStore()
  const { setPreferences, getPreference } = preferencesStore
  const outgoingCallsHandlingCondition = ref<OutgoingCallsHandlingConditions>(OutgoingCallsHandlingConditions.DEFAULT)
  const isOutgoingCallsHandlingConditionLoading = ref(false)
  const defaultTerminalLoading = ref(false)
  const defaultTerminalId = ref(WEB_CLIENT_TERMINAL_ID)

  const _syncFromPreferences = () => {
    const preferenceCondition = getPreference(PreferenceKey.OUTGOING_CALLS_HANDLING_CONDITION) as OutgoingCallsHandlingConditions | undefined
    const preferenceDefaultTerminalId = getPreference(PreferenceKey.DEFAULT_TERMINAL_ID) as number | undefined

    outgoingCallsHandlingCondition.value = preferenceCondition ?? OutgoingCallsHandlingConditions.DEFAULT
    defaultTerminalId.value = preferenceDefaultTerminalId ?? WEB_CLIENT_TERMINAL_ID
  }

  const fetchTerminals = async (): Promise<void> => {
    try {
      const fetch = useFetchApi()
      const { data } = await fetch.get<Array<Terminal>>(getAppURL({ pathName: '/api/user-terminal' }))

      terminals.value = data
    } catch (e) {
      console.error(e)
    }
  }

  const updateFields = async (guid: string, fields: TerminalFields): Promise<void> => {
    try {
      const payload: TerminalFieldPayload[] = []

      for (const key in fields) {
        payload.push({
          value: fields[key as keyof Terminal],
          path: key as TerminalFieldsKeys,
          op: 'replace',
        })
      }

      const fetch = useFetchApi()
      await fetch.patch(getAppURL({ pathName: `/api/user-terminal/${guid}` }), payload)
    } catch (e) {
      console.error(e)
    }
  }

  const updateTerminalReceivingCalls = async (guid: string, value: boolean): Promise<void> => {
    try {
      await useFetchApi().post(getAppURL({ pathName: `/api/user-terminal/${guid}` }), { enabledReceivingCall: value })

      const terminal = terminals.value.find(t => t.guid === guid)
      if (terminal) {
        terminal.enabledReceivingCall = value
      }
    } catch (e) {
      console.error(e)
    }
  }

  const setOutgoingCallsHandlingCondition = async (condition: OutgoingCallsHandlingConditions) => {
    try {
      isOutgoingCallsHandlingConditionLoading.value = true
      await setPreferences(PreferenceKey.OUTGOING_CALLS_HANDLING_CONDITION, condition)
    } catch (e) {
      console.error(e)
    } finally {
      isOutgoingCallsHandlingConditionLoading.value = false
    }
  }

  const connectedTerminals = computed(() => terminals.value.filter(t => t.status === 'connected'))

  const setDefaultTerminalId = async (id: number) => {
    try {
      defaultTerminalLoading.value = true
      await setPreferences(PreferenceKey.DEFAULT_TERMINAL_ID, id)
    } catch (e) {
      console.error(e)
    } finally {
      defaultTerminalLoading.value = false
    }
  }

  const getTerminalById = (id: number) => terminals.value.find(t => t.id === id)

  const defaultTerminalLabel = computed(() => {
    const terminal = getTerminalById(defaultTerminalId.value)
    if (!terminal) {
      return defaultTerminalId.value
    }

    return terminal.description ? terminal.description : `${terminal.deviceName} ${terminal.ipAddress}`
  })

  const showTerminalNotification = computed(() => outgoingCallsHandlingCondition.value === OutgoingCallsHandlingConditions.DEFAULT && defaultTerminalId.value !== WEB_CLIENT_TERMINAL_ID)

  watch(() => preferencesStore.isLoaded, (isLoaded) => {
    if (isLoaded) {
      _syncFromPreferences()
    }
  }, { immediate: true })

  watch(
    [
      () => getPreference(PreferenceKey.OUTGOING_CALLS_HANDLING_CONDITION),
      () => getPreference(PreferenceKey.DEFAULT_TERMINAL_ID),
    ],
    () => {
      if (!preferencesStore.isLoaded) {
        return
      }

      _syncFromPreferences()
    },
  )

  watch(outgoingCallsHandlingCondition, (condition) => {
    if (getPreference(PreferenceKey.OUTGOING_CALLS_HANDLING_CONDITION) === condition) return
    setOutgoingCallsHandlingCondition(condition)
  })
  watch(defaultTerminalId, (id) => {
    if (getPreference(PreferenceKey.DEFAULT_TERMINAL_ID) === id) return
    setDefaultTerminalId(id)
  })
  watch(terminals, v => {
    if (!defaultTerminalId.value) {
      return
    }
    // При изменении списка терминалов ищем выбранный по-умолчанию терминал, если его нет, то устанавливаем
    // id терминала веб-клиента (всегда считаем, что он есть, ведь мы с него сидим)
    const defaultTerminal = v.find(t => t.id === defaultTerminalId.value)
    if (!defaultTerminal) {
      defaultTerminalId.value = WEB_CLIENT_TERMINAL_ID
    }
  })

  return {
    terminals,
    outgoingCallsHandlingCondition,
    isOutgoingCallsHandlingConditionLoading,
    defaultTerminalId,
    defaultTerminalLoading,
    fetchTerminals,
    updateFields,
    updateTerminalReceivingCalls,
    setOutgoingCallsHandlingCondition,
    setDefaultTerminalId,
    defaultTerminalLabel,
    showTerminalNotification,
    connectedTerminals,
  }
})
